import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Download, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";

const DURATION_MS = 10000;
const TABS = [
  { id: 0, label: "Página pública", start: 0, end: 3333 },
  { id: 1, label: "Inscrição confirmada", start: 3333, end: 6666 },
  { id: 2, label: "Gestão no painel", start: 6666, end: 10000 },
];

export function InterestListHowItWorksDemo() {
  const [elapsed, setElapsed] = useState(0);
  const lastTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const animate = (timestamp: number) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = timestamp;
      }
      const delta = timestamp - lastTimeRef.current;
      lastTimeRef.current = timestamp;

      setElapsed((prev) => (prev + delta) % DURATION_MS);
      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const currentStep = elapsed < 3333 ? 0 : elapsed < 6666 ? 1 : 2;

  const handleSelectTab = (startMs: number) => {
    setElapsed(startMs);
    lastTimeRef.current = null;
  };

  const getTabProgress = (tab: (typeof TABS)[number]) => {
    if (elapsed >= tab.end) return 100;
    if (elapsed <= tab.start) return 0;
    return Math.min(100, Math.max(0, ((elapsed - tab.start) / (tab.end - tab.start)) * 100));
  };

  return (
    <div className="w-full flex flex-col rounded-2xl border border-border/60 bg-secondary/20 overflow-hidden select-none">
      {/* Abas com Barra de Progresso Única Segmentada */}
      <div className="grid grid-cols-3 border-b border-border/50 bg-background/50">
        {TABS.map((tab) => {
          const isActive = currentStep === tab.id;
          const progress = getTabProgress(tab);
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleSelectTab(tab.start)}
              className={cn(
                "group relative py-3 px-2 text-center text-xs transition-colors cursor-pointer",
                isActive ? "text-foreground font-semibold" : "text-muted-foreground hover:text-foreground font-normal"
              )}
            >
              <span className="block truncate">{tab.label}</span>
              {/* Linha de progresso única para cada etapa */}
              <div className="absolute inset-x-0 bottom-0 h-[2px] bg-secondary">
                <div
                  className="h-full bg-foreground transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </button>
          );
        })}
      </div>

      {/* Conteúdo em Mockup Realista */}
      <div className="relative h-[290px] w-full p-4 flex items-center justify-center">
        <AnimatePresence mode="wait">
          {/* FASE 1: Página Pública de Cadastro */}
          {currentStep === 0 && (
            <motion.div
              key="step-0"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-[320px] rounded-xl border border-border/70 bg-card p-4 space-y-3 shadow-xs"
            >
              {/* Barra de URL da página */}
              <div className="flex items-center justify-between text-[11px] text-muted-foreground pb-2 border-b border-border/40">
                <span className="truncate font-mono">tickethall.com.br/festival-sp</span>
                <ExternalLink className="h-3 w-3 shrink-0 ml-1 opacity-60" />
              </div>

              <div>
                <p className="text-xs font-semibold text-foreground">Festival de Verão 2026</p>
                <p className="text-[11px] text-muted-foreground">Cadastre-se para ser avisado da abertura de vendas</p>
              </div>

              <div className="space-y-2">
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">Nome</label>
                  <div className="h-8 rounded-lg border border-border/60 bg-background px-2.5 flex items-center text-xs text-foreground">
                    Mariana Souza
                  </div>
                </div>
                <div>
                  <label className="text-[10px] text-muted-foreground block mb-1">E-mail</label>
                  <div className="h-8 rounded-lg border border-border/60 bg-background px-2.5 flex items-center text-xs text-foreground">
                    mariana@email.com
                  </div>
                </div>
              </div>

              <div className="pt-1">
                <div className="h-8 rounded-lg bg-primary text-primary-foreground text-xs font-medium flex items-center justify-center">
                  Tenho interesse
                </div>
              </div>
            </motion.div>
          )}

          {/* FASE 2: Inscrição Confirmada */}
          {currentStep === 1 && (
            <motion.div
              key="step-1"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-[320px] rounded-xl border border-border/70 bg-card p-6 text-center space-y-3 shadow-xs"
            >
              <div className="h-10 w-10 mx-auto rounded-full bg-secondary flex items-center justify-center text-foreground">
                <Check className="h-5 w-5" />
              </div>

              <div className="space-y-1">
                <p className="text-sm font-semibold text-foreground">Interesse registrado</p>
                <p className="text-xs text-muted-foreground leading-relaxed">
                  Os dados foram salvos. O participante receberá a notificação assim que o primeiro lote estiver disponível.
                </p>
              </div>

              <div className="pt-2">
                <div className="rounded-lg bg-secondary/50 p-2.5 text-[11px] text-muted-foreground">
                  Inscrição vinculada ao evento Festival de Verão 2026
                </div>
              </div>
            </motion.div>
          )}

          {/* FASE 3: Painel do Produtor */}
          {currentStep === 2 && (
            <motion.div
              key="step-2"
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="w-full max-w-[340px] rounded-xl border border-border/70 bg-card p-3.5 space-y-3 shadow-xs"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-foreground">Inscrições recebidas</p>
                  <p className="text-[11px] text-muted-foreground">Base atualizada</p>
                </div>
                <div className="h-7 rounded-md border border-border/70 bg-background px-2.5 flex items-center gap-1.5 text-[11px] font-medium text-foreground">
                  <Download className="h-3 w-3 text-muted-foreground" />
                  <span>Exportar CSV</span>
                </div>
              </div>

              {/* Tabela de contatos */}
              <div className="divide-y divide-border/40 border border-border/40 rounded-lg overflow-hidden text-[11px]">
                <div className="bg-secondary/40 px-2.5 py-1.5 flex justify-between font-medium text-muted-foreground">
                  <span>Nome</span>
                  <span>Contato</span>
                </div>
                <div className="px-2.5 py-2 flex justify-between items-center bg-background">
                  <span className="font-medium text-foreground">Mariana Souza</span>
                  <span className="text-muted-foreground">mariana@email.com</span>
                </div>
                <div className="px-2.5 py-2 flex justify-between items-center bg-background">
                  <span className="font-medium text-foreground">Lucas Silva</span>
                  <span className="text-muted-foreground">(11) 98765-4321</span>
                </div>
                <div className="px-2.5 py-2 flex justify-between items-center bg-background">
                  <span className="font-medium text-foreground">Beatriz Lima</span>
                  <span className="text-muted-foreground">beatriz@email.com</span>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default InterestListHowItWorksDemo;
